/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import axios from "axios";
import { setupInterceptorsTo } from "./interceptors";

/**
 * Returns the configured API base URL.
 * Single source of truth used by ApiService, UserApiService, and the refresh interceptor.
 */
export const getApiBaseUrl = () => {
  const apiBaseUrl = process.env.REACT_APP_API_URL || '';
  return apiBaseUrl ? `${apiBaseUrl}/api` : '/api';
};

export default class ApiService {
  constructor(pathPrefix) {
    this.pathPrefix = pathPrefix;
    this.instance = axios.create({ baseURL: getApiBaseUrl() });
    setupInterceptorsTo(this.instance);
  }

  /**
   * Build a request path from the prefix + caller url, always ending in exactly
   * one trailing slash and with any accidental duplicate slashes collapsed.
   * Paths are relative (no protocol), so collapsing `//` -> `/` is always safe
   * and guards against callers that mistakenly include a leading/trailing slash.
   */
  buildPath = (url, withoutPrefix) => {
    const prefix = !withoutPrefix ? this.pathPrefix : "";
    return `/${prefix}${url}/`.replace(/\/{2,}/g, "/");
  };

  get = async (url, query = {}, withoutPrefix, config = {}) => {
    return await this.instance.get(this.buildPath(url, withoutPrefix), {
      ...config,
      params: query,
    });
  };

  post = async (url, data = {}, isJson = true, withoutPrefix, config = {}) => {
    return await this.instance.post(
      this.buildPath(url, withoutPrefix),
      isJson ? data : this.makeFormData(data),
      config
    );
  };

  put = async (url, data = {}, withoutPrefix) => {
    return await this.instance.put(this.buildPath(url, withoutPrefix), data);
  };

  patch = async (url, data = {}, withoutPrefix, params) => {
    return await this.instance.patch(this.buildPath(url, withoutPrefix), data, {
      params,
    });
  };

  delete = async (url, query = {}) => {
    // Pass query params as { params } so Axios appends them to the URL query string.
    return await this.instance.delete(this.buildPath(url, false), { params: query });
  };

  makeFormData = (data) => {
    const formData = new FormData();
    const appendFormData = (dataToAppend, path = "") => {
      Object.entries(dataToAppend || {}).forEach(([name, value]) => {
        const newPath = path ? `${path}[${name}]` : name;
        if (name.includes("file") || name === "image" || name === "pdf") {
          formData.append(newPath, value);
        } else if (typeof value === "object") {
          appendFormData(value, newPath);
        } else if (typeof value === "boolean") {
          formData.append(newPath, value ? "1" : "0");
        } else {
          formData.append(newPath, value);
        }
      });
    };
    appendFormData(data);

    return formData;
  };
}
